import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { STORIES } from '../../../routes/routeConstant'
import { getProfile, fetchStripeCheckOutSession } from '../../../redux/actions/myAccountAction'
import { SUPPORT_EMAIL } from '../../../utils/config'
import { membershipNameType } from '../../../utils/constant'
import { AppUseSelector } from '../../../redux/hooks'
import { trackConversion, isNewSubscription, isUpgrade } from '../../../utils/analytics'
import { captureWeeklyEvent } from '../../../utils/posthogWeekly'
import Loader from 'containers/loader/loader'
import InviteFriendsCard from '../../referral/InviteFriendsCard'

const BOOKING_EMBED_URL = 'https://book.maayot.com'
// Trafft "Pro onboarding" service for web payers (the apps use their own, b484b518…).
const BOOKING_UUID = 'f626662b-36e1-4324-a16e-815785a73366'
const PRO_ONBOARDING_SESSION_STORAGE_KEY = 'maayot_pro_onboarding_session_confirmed'

const PaymentSuccessful: React.FC = () => {
  const { t } = useTranslation('plan')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loader, setLoader] = useState(true)
  const [planName, setPlanName] = useState('')
  const [hasConfirmedScheduledSession, setHasConfirmedScheduledSession] = useState(false)
  const [hasCheckedScheduledSession, setHasCheckedScheduledSession] = useState(false)
  const { credentials } = AppUseSelector(({ user }) => user)
  const hasTrackedRef = useRef(false)
  const bookingContainerRef = useRef<HTMLDivElement>(null)

  const isPro = planName === membershipNameType.Pro
  const onboardingSessionStorageKey = `${PRO_ONBOARDING_SESSION_STORAGE_KEY}_${credentials?.memberId || 'anonymous'}`

  const handleGoToStories = () => {
    navigate(STORIES)
  }

  const handleConfirmScheduledSession = () => {
    localStorage.setItem(onboardingSessionStorageKey, 'true')
    setHasConfirmedScheduledSession(true)
    setHasCheckedScheduledSession(true)
    navigate(STORIES)
  }

  // Load the Trafft booking embed script once we know it's a Pro plan
  const loadBookingEmbed = useCallback(() => {
    if (!bookingContainerRef.current) return

    // Check if script already loaded
    const existingScript = document.querySelector('script[src="https://book.maayot.com/embed.js"]')
    if (existingScript) {
      // Script already in DOM — re-trigger by removing data-processed and re-running
      const el = bookingContainerRef.current.querySelector('.embedded-booking')
      if (el) {
        el.removeAttribute('data-processed')
      }
      // Remove and re-add to re-trigger
      existingScript.remove()
    }

    const script = document.createElement('script')
    script.src = 'https://book.maayot.com/embed.js'
    script.async = true
    document.body.appendChild(script)
  }, [])

  useEffect(() => {
    if (isPro && !loader) {
      // Small delay to ensure the DOM element is rendered
      const timer = setTimeout(loadBookingEmbed, 100)
      return () => clearTimeout(timer)
    }
  }, [isPro, loader, loadBookingEmbed])

  useEffect(() => {
    if (!isPro || loader) return

    const hasConfirmedSession = localStorage.getItem(onboardingSessionStorageKey) === 'true'
    setHasConfirmedScheduledSession(hasConfirmedSession)
    setHasCheckedScheduledSession(hasConfirmedSession)
  }, [isPro, loader, onboardingSessionStorageKey])

  useEffect(() => {
    const trackPaymentConversion = async () => {
      // Prevent multiple executions
      if (hasTrackedRef.current) return
      hasTrackedRef.current = true

      // Extract values from searchParams and credentials at the start
      const sessionId = searchParams.get('session_id')
      const previousPlan = searchParams.get('previous_plan') || 'maayot Free'
      // credentials.memberId can be empty on a fresh load after the Stripe redirect (it isn't
      // persisted across full page loads). We refresh the profile below, so fall back to that
      // fresh id rather than tracking conversions with an empty user_id.
      let userId = credentials?.memberId

      let actualAmount = 0
      let interval: 'month' | 'year' = 'month'
      let newPlan = ''

      // For direct upgrades (no checkout session), amount and interval come from URL params
      const urlAmount = searchParams.get('amount')
      const urlInterval = searchParams.get('interval')
      if (urlAmount) {
        actualAmount = parseFloat(urlAmount)
      }
      if (urlInterval === 'year' || urlInterval === 'month') {
        interval = urlInterval
      }

      // Fetch actual amount from Stripe session (overrides URL params if available)
      if (sessionId) {
        try {
          const session = await fetchStripeCheckOutSession({ sessionId })
          if (session) {
            // amount_total is in cents
            actualAmount = (session.amount_total || 0) / 100
            // Handle trial (amount_total may be 0 for trial start)
            if (actualAmount === 0 && session.subscription) {
              // This is a trial start - track it now that checkout is confirmed
              trackConversion({
                event: 'start_trial',
                value: 0,
                plan_name: 'maayot Pro Trial',
                user_id: userId,
                transaction_id: sessionId || undefined,
              })
            }
            // Determine interval from session
            // Note: subscription might be a string ID or an object, depending on expansion
            if (session.mode === 'subscription') {
              if (typeof session.subscription === 'string') {
                // Subscription is just an ID, default to month (will be corrected by server-side tracking)
                interval = 'month'
              } else if (session.subscription?.plan?.interval) {
                interval = session.subscription.plan.interval === 'year' ? 'year' : 'month'
              } else {
                interval = 'month'
              }
            } else {
              interval = 'month'
            }
          }
        } catch (error) {
          console.error('Failed to fetch checkout session:', error)
        }
      }

      // Refresh profile to get new plan
      try {
        const profileData = await getProfile()
        // Prefer the freshly fetched id in case credentials.memberId wasn't populated yet.
        if (!userId && profileData?.id) {
          userId = profileData.id
        }
        // Use the return value directly to avoid race conditions with localStorage
        if (profileData?.membership?.name) {
          newPlan = profileData.membership.name
        } else {
          // Fallback to localStorage if return value doesn't have it
          const userData = localStorage.getItem('user')
          if (userData) {
            const parsed = JSON.parse(userData)
            newPlan = parsed?.membership?.name || ''
          }
        }
      } catch (error) {
        console.error('Failed to get profile:', error)
        // Fallback to localStorage on error
        const userData = localStorage.getItem('user')
        if (userData) {
          const parsed = JSON.parse(userData)
          newPlan = parsed?.membership?.name || ''
        }
      }

      // Only track if there was an actual charge (not trial start)
      if (actualAmount > 0 && newPlan) {
        // U9: funnel — Weekly purchase confirmed (diagnostic; server webhook is source of truth).
        if (newPlan === membershipNameType.Weekly) {
          captureWeeklyEvent('weekly_purchase_succeeded', { amount: actualAmount, interval })
        }
        if (isNewSubscription(previousPlan, newPlan)) {
          trackConversion({
            event: 'purchase',
            value: actualAmount,
            plan_name: newPlan,
            previous_plan: previousPlan,
            user_id: userId,
            transaction_id: sessionId || undefined,
            interval: interval,
          })
        } else if (isUpgrade(previousPlan, newPlan)) {
          trackConversion({
            event: 'upgrade',
            value: actualAmount,
            plan_name: newPlan,
            previous_plan: previousPlan,
            user_id: userId,
            interval: interval,
          })
        }
      }

      // Switching billing interval within Pro (monthly <-> yearly): the learner already had the
      // onboarding booking when they first joined Pro, so skip the gate and go straight to stories.
      if (newPlan === membershipNameType.Pro && previousPlan === membershipNameType.Pro) {
        navigate(STORIES, { replace: true })
        return
      }

      setPlanName(newPlan)
      setLoader(false)
    }

    trackPaymentConversion()
  }, []) // Run only once on mount

  if (loader) {
    return (
      <div className='payment-successful-container'>
        <div className='payment-successful-content'>
          <Loader showText={false} color='#068466' />
        </div>
      </div>
    )
  }

  // Pro plan: show onboarding experience with booking
  if (isPro) {
    return (
      <div className='pro-welcome-container'>
        <div className='pro-welcome-inner'>
          {/* Header section */}
          <div className='pro-welcome-header'>
            <div className='pro-welcome-header-content'>
              <div className='pro-welcome-check'>
                <svg width='28' height='28' viewBox='0 0 28 28' fill='none'>
                  <circle cx='14' cy='14' r='14' fill='#068466' />
                  <path
                    d='M8 14.5L12 18.5L20 10.5'
                    stroke='white'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <span className='pro-welcome-confirmed'>{t('success.pro.activated')}</span>
              </div>
              <h1 className='pro-welcome-title'>{t('success.pro.title')}</h1>
              <p className='pro-welcome-subtitle'>{t('success.pro.subtitle')}</p>
            </div>
          </div>

          {/* Main content area */}
          <div className='pro-welcome-body'>
            {/* Quick tips */}
            <div className='pro-welcome-tips'>
              <h3 className='pro-welcome-tips-title'>{t('success.pro.tipsTitle')}</h3>

              <div className='pro-welcome-tip'>
                <div className='pro-welcome-tip-number'>1</div>
                <div className='pro-welcome-tip-content'>
                  <h4>{t('success.pro.tips.pronunciation.title')}</h4>
                  <p>{t('success.pro.tips.pronunciation.body')}</p>
                </div>
              </div>

              <div className='pro-welcome-tip'>
                <div className='pro-welcome-tip-number'>2</div>
                <div className='pro-welcome-tip-content'>
                  <h4>{t('success.pro.tips.writing.title')}</h4>
                  <p>{t('success.pro.tips.writing.body')}</p>
                </div>
              </div>

              <div className='pro-welcome-tip'>
                <div className='pro-welcome-tip-number'>3</div>
                <div className='pro-welcome-tip-content'>
                  <h4>{t('success.pro.tips.corrections.title')}</h4>
                  <p>{t('success.pro.tips.corrections.body')}</p>
                </div>
              </div>
            </div>

            {/* Booking section */}
            <div className='pro-welcome-booking' ref={bookingContainerRef}>
              <h3 className='pro-welcome-booking-title'>{t('success.pro.bookingTitle')}</h3>
              <p className='pro-welcome-booking-desc'>{t('success.pro.bookingDesc')}</p>
              <p className='pro-welcome-booking-desc'>{t('common:teacherLanguageNotice')}</p>
              <div
                className='embedded-booking'
                data-url={BOOKING_EMBED_URL}
                data-query={`&t=s&uuid=${BOOKING_UUID}`}
                data-lang='en'
                data-autoresize='0'
                data-showsidebar='1'
                data-showservices='0'
                style={{ minWidth: '320px', height: '768px' }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className='pro-welcome-footer'>
            {hasConfirmedScheduledSession ? (
              <>
                <div className='pro-welcome-gate-success'>
                  <i className='fa fa-check-circle' />
                  {t('success.pro.marked')}
                </div>
                <button className='pro-welcome-confirm-btn' onClick={handleGoToStories}>
                  <i className='fa fa-book' />
                  {t('success.pro.goToLessons')}
                </button>
              </>
            ) : (
              <>
                <label className='pro-welcome-gate-checkbox'>
                  <input
                    type='checkbox'
                    checked={hasCheckedScheduledSession}
                    onChange={event => setHasCheckedScheduledSession(event.target.checked)}
                  />
                  <span>{t('success.pro.checkbox')}</span>
                </label>
                <button
                  className='pro-welcome-confirm-btn'
                  onClick={handleConfirmScheduledSession}
                  disabled={!hasCheckedScheduledSession}
                >
                  <i className='fa fa-check' />
                  {t('success.pro.confirm')}
                </button>
                <p className='pro-welcome-gate-note'>{t('success.pro.gateNote')}</p>
              </>
            )}
            <p className='pro-welcome-footer-support'>
              <Trans
                t={t}
                i18nKey='success.pro.support'
                values={{ email: SUPPORT_EMAIL }}
                components={[<a key='email' href={`mailto:${SUPPORT_EMAIL}`} />]}
              />
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Non-Pro plans: existing confirmation screen
  return (
    <div className='payment-successful-container'>
      <div className='payment-successful-content'>
        <div className='payment-successful-icon'>
          <i className='fa fa-check-circle'></i>
        </div>

        <h2 className='payment-successful-title'>{t('success.title')}</h2>

        <p className='payment-successful-message'>{t('success.message')}</p>

        <div className='payment-successful-actions'>
          <button className='payment-successful-stories-btn' onClick={handleGoToStories}>
            <span>
              <i className='fa fa-book' />
              {t('success.startLesson')}
            </span>
          </button>
        </div>

        <InviteFriendsCard placement='payment_successful' />

        <p className='payment-successful-support'>
          <Trans
            t={t}
            i18nKey='success.support'
            values={{ email: SUPPORT_EMAIL }}
            components={[<a key='email' href={`mailto:${SUPPORT_EMAIL}`} />]}
          />
        </p>
      </div>
    </div>
  )
}

export default PaymentSuccessful
